import React, { useRef, useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Spinner } from "./ui.jsx";
import { primaryBtnStyle, secondaryBtnStyle } from "./styles.js";
import { categoryMeta } from "../domain/categories.js";
import { scanPhoto, mergeScannedMaterials } from "../domain/preservationScan.js";

/* Reads a photo of one item and proposes materials and care notes.

   The result is always reviewed before it lands. Claude is looking at one
   photo of one side of a piece someone owns and has handled — they know things
   the photo cannot show, so the scan proposes and the collector decides. It
   also merges rather than replaces: a scan adds what it saw, and never erases
   a material entered by hand from a tag no camera caught. */
export default function PreservationScanner({ item, onApply }) {
  const [state, setState] = useState({ status: "idle" });
  const inputRef = useRef(null);

  const meta = categoryMeta(item.category);
  const groupTitle = (key) => meta.groups?.find((g) => g.key === key)?.title?.split("(")[0].trim() || key;

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // so re-picking the same photo still fires a change
    if (!file) return;

    setState({ status: "scanning" });
    const { result, error } = await scanPhoto(file, item);
    if (error) {
      setState({ status: "error", error });
      return;
    }
    setState({ status: "done", result });
  }

  async function apply() {
    const materials = mergeScannedMaterials(item.materials, state.result.identifiedMaterials);
    setState({ status: "applying" });
    const { error } = await onApply(item.id, materials);
    setState(error ? { status: "error", error } : { status: "idle" });
  }

  const found = Object.entries(state.result?.identifiedMaterials || {});
  const busy = state.status === "scanning" || state.status === "applying";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: "none" }}
      />

      {state.status !== "done" && (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            style={{ ...secondaryBtnStyle, textAlign: "center", opacity: busy ? 0.6 : 1, cursor: busy ? "wait" : "pointer" }}
          >
            {state.status === "scanning" ? "Reading the photo…" : "✨  Scan a photo for materials"}
          </button>
          {state.status === "scanning" && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Spinner size={16} />
            </div>
          )}
          <span style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>
            Photograph the piece in good light. Nothing is changed until you review what comes back.
          </span>
        </>
      )}

      {state.status === "error" && (
        <span style={{ fontSize: 12, color: COLORS.red, fontFamily: FONT_BODY }}>{state.error}</span>
      )}

      {state.status === "done" && (
        <div
          style={{
            background: COLORS.blackCard,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 15.5, color: COLORS.goldBright }}>What the photo shows</div>

          {state.result.conditionNotes && (
            <div style={{ fontSize: 13, color: COLORS.cream, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
              {state.result.conditionNotes}
            </div>
          )}

          {found.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {found.map(([group, values]) => (
                <div key={group} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: COLORS.creamFaint }}>
                    {groupTitle(group)}
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {values.map((v) => (
                      <span
                        key={v}
                        style={{
                          fontSize: 12, fontFamily: FONT_BODY, color: COLORS.goldBright,
                          background: "rgba(201,162,39,0.12)", border: `1px solid ${COLORS.line}`,
                          borderRadius: 999, padding: "3px 10px",
                        }}
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: COLORS.creamDim, fontFamily: FONT_BODY }}>
              No materials could be identified from this photo — the care notes below are still worth reading.
            </div>
          )}

          {state.result.preservationTips?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {state.result.preservationTips.map((tip, i) => (
                <div key={i} style={{ fontSize: 12.5, color: COLORS.creamDim, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
                  • {tip}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={() => setState({ status: "idle" })} style={secondaryBtnStyle}>
              Discard
            </button>
            <button
              type="button"
              disabled={!found.length || state.status === "applying"}
              onClick={apply}
              style={{ ...primaryBtnStyle, opacity: found.length ? 1 : 0.5 }}
            >
              {state.status === "applying" ? "Saving…" : "Add these materials"}
            </button>
          </div>
          <span style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>
            Adding keeps everything already recorded — it only fills in what the photo confirmed.
          </span>
        </div>
      )}
    </div>
  );
}
